import { logger } from '@/lib/logger';
import React, { useEffect, useState, Suspense, lazy, createContext, startTransition } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as HotToaster } from 'react-hot-toast';
import { DomainProvider, useDomain } from '@/lib/domain-context';
import { NavbarProvider } from '@/contexts/NavbarContext';
import { CloseProvider } from '@/contexts/close-context';
import { PaywallProvider } from '@/contexts/paywall-context';
import { LayoutDebugger } from '@/components/LayoutDebugger';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { unleashEnabled } from '@/lib/feature-flags';
import { ThemeProvider } from '@/lib/theme-provider';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { LearningProfileProvider, OnboardingModalHost } from '@/contexts/LearningProfileContext';
import { logEnvironmentInfo } from '@/lib/environment';
import { useAnalytics } from '@/hooks/use-analytics';
import { useUTMTracking } from '@/hooks/use-utm-tracking';
import { OpenAiPixelLoader } from '@/components/analytics/OpenAiPixelLoader';


// Log environment information on app startup
if (import.meta.env.DEV) {
  logEnvironmentInfo();
}

// Direct imports for homepage components
// `/` — the root homepage. STATICALLY imported, NOT lazy, and that is a
// deliberate reversal of the pattern every other marketing page here follows.
// `/` is the highest-value route on the site (80 of the 85 organic clicks in the
// 90 days before the cutover, per docs/marketing/seo-baseline-2026-09-pre-cutover.md).
// A lazy route makes the LCP wait on a second network round trip for the chunk
// AFTER the entry bundle has already parsed; on the one page where that cost is
// paid by every first-time visitor, the bundle-size saving is the wrong trade.
// Every OTHER route below stays lazy for exactly the reason this one is not.
import { HomeVariantBPage } from '@/pages/marketing/HomeVariantBPage';

// Unlisted TLD-hub prototype for founder review (/grok). noindex. Do not
// promote into SEO_ROUTES until a homepage cutover is approved.
const GrokHubPage = lazy(() => import('@/pages/GrokHubPage').then(m => ({ default: m.GrokHubPage })));
const GrokCombinedPage = lazy(() => import('@/pages/GrokCombinedPage').then(m => ({ default: m.GrokCombinedPage })));
const GrokSubPage = lazy(() => import('@/pages/GrokSubPage').then(m => ({ default: m.GrokSubPage })));
const GrokLawSchoolPage = lazy(() => import('@/pages/GrokProductPages').then(m => ({ default: m.GrokLawSchoolPage })));
const GrokJdsUnlimitedPage = lazy(() => import('@/pages/GrokProductPages').then(m => ({ default: m.GrokJdsUnlimitedPage })));
const GrokBarPracticePage = lazy(() => import('@/pages/GrokProductPages').then(m => ({ default: m.GrokBarPracticePage })));
const GrokBarOfficialQuestionsPage = lazy(() => import('@/pages/GrokProductPages').then(m => ({ default: m.GrokBarOfficialQuestionsPage })));
const GrokNextgenCoursePage = lazy(() => import('@/pages/GrokProductPages').then(m => ({ default: m.GrokNextgenCoursePage })));

// Lazy-load legal pages (rarely accessed, don't need in main bundle)
const TermsOfService = lazy(() => import('@/pages/TermsOfService').then(module => ({ default: module.TermsOfService })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const Disclaimer = lazy(() => import('@/pages/Disclaimer').then(module => ({ default: module.Disclaimer })));

// Lazy-load password reset pages
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })));

// Lazy-load PersistentLayout — only mounted on authenticated routes.
// Eagerly importing it pulled the entire sidebar/navbar/dashboard tree into
// the entry bundle, hurting homepage LCP. Routes that use it as a layout
// element still work — React Router suspends on the layout's render until
// the chunk loads, then renders the matched <Outlet />.
const PersistentLayout = lazy(() => import('@/components/layout/PersistentLayout').then(module => ({ default: module.PersistentLayout })));

// FlagGate — Unleash flag-gated render wrapper. Use for route-level gating
// (F.10 wrap). Layered ON TOP of existing ProtectedRoute / NextGenContentGate
// / entitlement checks — those stay intact.
import { FlagGate } from '@/components/guards/FlagGate';

// Import ScrollToTop for universal route changes
import ScrollToTop from '@/components/flashcards/ScrollToTop';

// Lazy load large components
// Note: The React.StrictMode in main.tsx causes components to mount twice in development
// This is normal and helps catch side effect bugs. It does NOT indicate duplicate components in production.
// IMPORTANT: AuthProvider is already present at the app root level. DO NOT add additional AuthProviders in route components.
const AuthPage = lazy(() => import('@/components/auth/AuthPage').then(module => ({ default: module.AuthPage })));
const FlashcardsPage = lazy(() => import('@/components/flashcards/FlashcardsPageWrapper'));
const CoursesPage = lazy(() => import('@/components/courses/CoursesPageWrapper'));
// Lazy load admin components to keep them out of main bundle
const LazyAdminCourseDetail = lazy(() => import('./components/admin/CourseDetail').then(m => ({ default: m.CourseDetail })));
const PublicCourseDetail = lazy(() => import('@/components/courses/CourseDetail'));
const SubscriptionSuccess = lazy(() => import('@/components/SubscriptionSuccess').then(module => ({ default: module.SubscriptionSuccess })));

// Lazy-load ChatContainer (only used on /chat route)
const ChatContainer = lazy(() => import('@/components/chat/ChatContainer'));

// Lazy-load Dashboards — authenticated-only routes, no reason to ship in entry bundle.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu) — was wasting
// ~150KB+ of homepage payload. All callers already wrap in <Suspense>.
const JDSDashboard = lazy(() => import('@/pages/Dashboard'));
const IntelligenceDashboard = lazy(() => import('@/pages/IntelligenceDashboard'));

// Lazy load diagnostic page
const DiagnosticPage = lazy(() => import('@/pages/diagnostic/DiagnosticPage'));

// Note: Syllabus page moved to /dashboard/classes (ClassesTabContent)

// Lazy load study plan page
const StudyPlanPage = lazy(() => import('@/pages/study-plan/StudyPlanPage'));
const StudyPlanRedirect = lazy(() => import('@/pages/study-plan/StudyPlanRedirect'));

// Lazy load score import page
const ImportScoresPage = lazy(() => import('@/pages/score-import/ImportScoresPage'));

// Import directly for debugging purposes
// import JDSDashboardDirect from '@/pages/Dashboard';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { UpdateBanner } from '@/components/UpdateBanner';
import {
  Routes,
  Route,
  Navigate,
  BrowserRouter,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { initializeDeepLinks } from '@/lib/capacitor/deep-links';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NativePurchaseGuard } from '@/components/native';

// Forces NextGenMCQPracticePage to remount on every navigation so drill state
// (drillAutoStarted ref, infiniteSession, pageState) resets cleanly.
const MCQPracticeWithReset = () => {
  const { key } = useLocation();
  return <NextGenMCQPracticePage key={key} />;
};
import { CourseRedirect } from './components/courses/CourseRedirect';

// Lazy-load SettingsPage — authenticated-only, kept out of entry bundle.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const SettingsPage = lazy(() => import('@/components/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
// Import trial banner for global display
import { GlobalTrialBanner } from '@/components/subscription/TrialBanner';
import { TrialBannerProvider } from '@/contexts/TrialBannerContext';
// Import our admin components
const AdminDashboard = lazy(() => import('@/components/admin/Dashboard').then(module => ({ default: module.default })));
const AdminUsers = lazy(() => import('@/components/admin/Users').then(module => ({ default: module.default })));
const AdminUserDetailPage = lazy(() => import('@/pages/Admin/AdminUserDetailPage').then(module => ({ default: module.default })));
const AdminWebAnalytics = lazy(() => import('@/components/admin/WebAnalytics').then(module => ({ default: module.default })));
const AdminErrorLogs = lazy(() => import('@/components/admin/ErrorLogs').then(module => ({ default: module.default })));
// JDS Features admin host — consolidates Courses overview / Flashcards / Outlines /
// Case Law behind one segmented-control page. Each CRUD tab lazy-mounts inside it,
// so AdminFlashcards/AdminOutlines/AdminCases are imported there, not here.
const JdsFeaturesPage = lazy(() => import('@/pages/Admin/JdsFeaturesPage').then(module => ({ default: module.JdsFeaturesPage })));
const AdminBlogManagement = lazy(() => import('@/components/admin/blog/BlogManagement').then(module => ({ default: module.BlogManagement })));
const BlogAIGenerator = lazy(() => import('@/pages/Admin/BlogAIGenerator').then(module => ({ default: module.BlogAIGenerator })));
const AdminPricing = lazy(() => import('@/components/admin/AdminPriceMapping').then(module => ({ default: module.default })));
const AdminContentHealth = lazy(() => import('@/components/admin/ContentHealth').then(module => ({ default: module.default })));
const AdminContentPipeline = lazy(() => import('@/pages/Admin/ContentPipelinePage').then(module => ({ default: module.default })));
const AdminTranscriptReview = lazy(() => import('@/pages/Admin/TranscriptReviewPage').then(module => ({ default: module.default })));
const AdminStudicataQa = lazy(() => import('@/pages/Admin/StudicataQaPage').then(module => ({ default: module.default })));
const AdminStudicataReview = lazy(() => import('@/pages/Admin/StudicataReviewPage').then(module => ({ default: module.default })));
const AdminCommunityModeration = lazy(() => import('@/components/admin/CommunityModeration').then(module => ({ default: module.default })));
// R28 — faculty case-flag review queue (Relationship Hub row pattern).
const AdminCaseFlags = lazy(() => import('@/pages/Admin/CaseFlagsPage').then(m => ({ default: m.CaseFlagsPage })));
const AdminSupport = lazy(() => import('@/components/admin/AdminSupport').then(module => ({ default: module.default })));
const SetAdminStatus = lazy(() => import('@/components/admin/SetAdmin').then(module => ({ default: module.default })));
const NextGenSalesPage = lazy(() => import('@/pages/Admin/NextGenSalesPage').then(m => ({ default: m.default })));
// Relationship Hub (internal CRM) — admin-only school pipeline + school detail.
const SchoolPipelinePage = lazy(() => import('@/pages/Admin/SchoolPipelinePage').then(m => ({ default: m.default })));
const SchoolHubPage = lazy(() => import('@/pages/Admin/SchoolHubPage').then(m => ({ default: m.default })));
const AdminTasksPage = lazy(() => import('@/pages/Admin/AdminTasksPage').then(m => ({ default: m.default })));
const TaskDetailPage = lazy(() => import('@/pages/Admin/TaskDetailPage').then(m => ({ default: m.default })));
const CrmReportsPage = lazy(() => import('@/pages/Admin/CrmReportsPage').then(m => ({ default: m.default })));

// Import debug components
const UsermavenDebug = lazy(() => import('@/components/debug/UsermavenDebug').then(module => ({ default: module.UsermavenDebug })));

// Import outline pages
const OutlineLibrary = lazy(() => import('@/pages/outlines/OutlineLibrary'));
const OutlineAnalyses = lazy(() => import('@/pages/outlines/OutlineAnalysesWrapper'));
const UploadOutlinePage = lazy(() => import('@/pages/outlines/UploadOutlinePage').then(module => ({ default: module.UploadOutlinePage })));
const OutlineAnalysisDetail = lazy(() => import('@/pages/outlines/OutlineAnalysisDetail').then(module => ({ default: module.OutlineAnalysisDetail })));
const OutlineArchive = lazy(() => import('@/pages/outlines/OutlineArchive'));

// Import NextGen bar prep pages
const NextGenLandingPage = lazy(() => import('@/pages/nextgen/NextGenLandingPage').then(module => ({ default: module.NextGenLandingPage })));
const NextGenUpgradePage = lazy(() => import('@/pages/nextgen/NextGenUpgradePage').then(module => ({ default: module.NextGenUpgradePage })));
const NextGenOfficialQuestionsPage = lazy(() => import('@/pages/nextgen/NextGenOfficialQuestionsPage').then(module => ({ default: module.NextGenOfficialQuestionsPage })));
const NextGenOfficialQuestionsSalesPage = lazy(() => import('@/pages/nextgen/NextGenOfficialQuestionsSalesPage').then(module => ({ default: module.NextGenOfficialQuestionsSalesPage })));
const NextGenPreviewPage = lazy(() => import('@/pages/nextgen/NextGenPreviewPage').then(module => ({ default: module.NextGenPreviewPage })));
const NextGenTaskDetail = lazy(() => import('@/pages/nextgen/NextGenTaskDetail').then(module => ({ default: module.NextGenTaskDetail })));
const NextGenActivatePage = lazy(() => import('@/pages/nextgen/NextGenActivatePage').then(module => ({ default: module.NextGenActivatePage })));
const NextGenPurchaseConfirmedPage = lazy(() => import('@/pages/nextgen/NextGenPurchaseConfirmedPage').then(module => ({ default: module.NextGenPurchaseConfirmedPage })));
// Workspace pages retired (2026-04-11) — replaced by NCBE-aligned practice system.
// NextGenWorkbenchPage / NextGenHeatmapPage / NextGenGradeReport deleted from disk 2026-05-04.
// Heatmap activity type fully removed 2026-05-25 (components, hook, DB tables, task rows).
// client_chat task type retired 2026-05-25 (board RYu91USgyqQZ3W7gj4qi). NextGenClientChatPage
// + useNextGenCounseling deleted from disk 2026-05-26 (zero rows in nextgen_tasks, no
// remaining consumers). Old /nextgen/client-chat/:taskId and /nextgen/practice/counseling/:taskId
// still redirect to /nextgen/practice/pt for backward compat with any stale bookmarks.
const NextGenDashboardPage = lazy(() => import('@/pages/nextgen/NextGenDashboardPage').then(module => ({ default: module.NextGenDashboardPage })));
const NextGenSkillsPage = lazy(() => import('@/pages/nextgen/NextGenSkillsPage').then(module => ({ default: module.NextGenSkillsPage })));
const NextGenProgressPage = lazy(() => import('@/pages/nextgen/NextGenProgressPage').then(module => ({ default: module.NextGenProgressPage })));
// Lazy-load CourseLibrary/CoursePlayer — only used inside NextGen coursework routes.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const CourseLibrary = lazy(() => import('@/components/courses/CourseLibrary').then(module => ({ default: module.CourseLibrary })));
const CoursePlayer = lazy(() => import('@/components/courses/CoursePlayer').then(module => ({ default: module.CoursePlayer })));
const NextGenSimulationsPage = lazy(() => import('@/pages/nextgen/NextGenSimulationsPage').then(module => ({ default: module.NextGenSimulationsPage })));
const NextGenFullExamPage = lazy(() => import('@/pages/nextgen/NextGenFullExamPage').then(module => ({ default: module.NextGenFullExamPage })));
const NextGenExamResultsPage = lazy(() => import('@/pages/nextgen/NextGenExamResultsPage').then(module => ({ default: module.NextGenExamResultsPage })));
const NextGenCoachPage = lazy(() => import('@/pages/nextgen/NextGenCoachPage'));
const NextGenExamMapPage = lazy(() => import('@/pages/nextgen/NextGenExamMapPage').then(module => ({ default: module.NextGenExamMapPage })));
const NextGenMCQPracticePage = lazy(() => import('@/pages/nextgen/NextGenMCQPracticePage').then(module => ({ default: module.NextGenMCQPracticePage })));
const NextGenIQSPracticePage = lazy(() => import('@/pages/nextgen/NextGenIQSPracticePage').then(module => ({ default: module.NextGenIQSPracticePage })));
const NextGenPTPracticePage = lazy(() => import('@/pages/nextgen/NextGenPTPracticePage').then(module => ({ default: module.NextGenPTPracticePage })));
const NextGenDraftingPracticePage = lazy(() => import('@/pages/nextgen/NextGenDraftingPracticePage').then(module => ({ default: module.NextGenDraftingPracticePage })));
const NextGenSectionSimPage = lazy(() => import('@/pages/nextgen/NextGenSectionSimPage').then(module => ({ default: module.NextGenSectionSimPage })));
const NextGenSimReviewPage = lazy(() => import('@/pages/nextgen/NextGenSimReviewPage').then(module => ({ default: module.NextGenSimReviewPage })));
const NextGenMCQSinglePage = lazy(() => import('@/pages/nextgen/NextGenMCQSinglePage').then(module => ({ default: module.NextGenMCQSinglePage })));
const NextGenSetRedirect = lazy(() => import('@/pages/nextgen/NextGenSetRedirect').then(module => ({ default: module.NextGenSetRedirect })));
const NextGenLayout = lazy(() => import('@/components/nextgen/NextGenLayout').then(module => ({ default: module.NextGenLayout })));
// Lazy-load NextGenContentGate — only used inside NextGen routes.
// Pulled out of main bundle 2026-05-25 (board 8iK8ZuVwbtHaa9Pvrtvu).
const NextGenContentGate = lazy(() => import('@/components/nextgen/NextGenContentGate').then(module => ({ default: module.NextGenContentGate })));

// Professor Console — 314 Formative Assessment Kit (board project 8WpxviKObWe89vsP1hXq).
// FacultyLayout self-gates on the `faculty-console` flag (FlagGate → Navigate to "/").
// Interim mount at /faculty/* until school.jdsimplified.com lands (spec D7);
// the route tree is domain-agnostic on purpose. Faculty entitlement is enforced
// server-side by the faculty-console service on every endpoint.
const FacultyLayout = lazy(() => import('@/components/faculty/FacultyLayout').then(m => ({ default: m.FacultyLayout })));
const FacultyClassesPage = lazy(() => import('@/pages/faculty/FacultyClassesPage').then(m => ({ default: m.FacultyClassesPage })));
const FacultyClassSetupPage = lazy(() => import('@/pages/faculty/FacultyClassSetupPage').then(m => ({ default: m.FacultyClassSetupPage })));
const FacultyAssignmentBuilderPage = lazy(() => import('@/pages/faculty/FacultyAssignmentBuilderPage').then(m => ({ default: m.FacultyAssignmentBuilderPage })));
const FacultyReviewQueuePage = lazy(() => import('@/pages/faculty/FacultyReviewQueuePage').then(m => ({ default: m.FacultyReviewQueuePage })));
const FacultyReviewPage = lazy(() => import('@/pages/faculty/FacultyReviewPage').then(m => ({ default: m.FacultyReviewPage })));
const FacultyOutcomeMappingPage = lazy(() => import('@/pages/faculty/FacultyOutcomeMappingPage').then(m => ({ default: m.FacultyOutcomeMappingPage })));
const FacultyAnalyticsPage = lazy(() => import('@/pages/faculty/FacultyAnalyticsPage').then(m => ({ default: m.FacultyAnalyticsPage })));
const FacultyCompliancePage = lazy(() => import('@/pages/faculty/FacultyCompliancePage').then(m => ({ default: m.FacultyCompliancePage })));
const FacultyRemediationPage = lazy(() => import('@/pages/faculty/FacultyRemediationPage').then(m => ({ default: m.FacultyRemediationPage })));
// R26/R27 scoped discussions (board oLDc8Jvo7dWHgJNkJBYE): class board + Faculty Room.
// Rail entries are integrated by the school-domain window; until then these are URL-reachable only.
const FacultyClassDiscussionPage = lazy(() => import('@/pages/faculty/discussion/FacultyClassDiscussionPage').then(m => ({ default: m.FacultyClassDiscussionPage })));
const FacultyRoomPage = lazy(() => import('@/pages/faculty/discussion/FacultyRoomPage').then(m => ({ default: m.FacultyRoomPage })));
const FacultyDeskPage = lazy(() => import('@/pages/faculty/FacultyDeskPage').then(m => ({ default: m.FacultyDeskPage })));
const FacultyAssignmentsPage = lazy(() => import('@/pages/faculty/FacultyAssignmentsPage').then(m => ({ default: m.FacultyAssignmentsPage })));
const FacultyClassStudentsPage = lazy(() => import('@/pages/faculty/FacultyClassStudentsPage').then(m => ({ default: m.FacultyClassStudentsPage })));
const FacultyStudentProfilePage = lazy(() => import('@/pages/faculty/FacultyStudentProfilePage').then(m => ({ default: m.FacultyStudentProfilePage })));
const FacultyMaterialsPage = lazy(() => import('@/pages/faculty/FacultyMaterialsPage').then(m => ({ default: m.FacultyMaterialsPage })));
const FacultyReviewInboxPage = lazy(() => import('@/pages/faculty/FacultyReviewInboxPage').then(m => ({ default: m.FacultyReviewInboxPage })));
const FacultyTemplateFromSyllabusPage = lazy(() => import('@/pages/faculty/FacultyTemplateFromSyllabusPage').then(m => ({ default: m.FacultyTemplateFromSyllabusPage })));
const FacultyProgramReportsPage = lazy(() => import('@/pages/faculty/FacultyProgramReportsPage').then(m => ({ default: m.FacultyProgramReportsPage })));
// Course templates + deploy wizard + currency report (R17/R18/R19, canvas 3b-3e)
const FacultyTemplatesPage = lazy(() => import('@/pages/faculty/FacultyTemplatesPage').then(m => ({ default: m.FacultyTemplatesPage })));
const FacultyTemplateDetailPage = lazy(() => import('@/pages/faculty/FacultyTemplateDetailPage').then(m => ({ default: m.FacultyTemplateDetailPage })));
const FacultyTemplateDeployPage = lazy(() => import('@/pages/faculty/FacultyTemplateDeployPage').then(m => ({ default: m.FacultyTemplateDeployPage })));
const FacultyCurrencyReportPage = lazy(() => import('@/pages/faculty/FacultyCurrencyReportPage').then(m => ({ default: m.FacultyCurrencyReportPage })));
// Kit student surfaces (main app, not /faculty/*): consent-first join + gated result (1f)
const JoinClassPage = lazy(() => import('@/pages/class-assignments/JoinClassPage').then(m => ({ default: m.JoinClassPage })));
const MyClassAssignmentsPage = lazy(() => import('@/pages/class-assignments/MyClassAssignmentsPage').then(m => ({ default: m.MyClassAssignmentsPage })));
const ClassAssignmentPage = lazy(() => import('@/pages/class-assignments/ClassAssignmentPage').then(m => ({ default: m.ClassAssignmentPage })));
const ClassHomePage = lazy(() => import('@/pages/class-assignments/ClassHomePage').then(m => ({ default: m.ClassHomePage })));
const ClassConsentSettingsPage = lazy(() => import('@/pages/class-assignments/ClassConsentSettingsPage').then(m => ({ default: m.ClassConsentSettingsPage })));
const ClassDiscussionPage = lazy(() => import('@/pages/class-assignments/ClassDiscussionPage').then(m => ({ default: m.ClassDiscussionPage })));
const ShareLandingPage = lazy(() => import('@/pages/class-assignments/ShareLandingPage').then(m => ({ default: m.ShareLandingPage })));

// UBE/MBE standalone bar-prep product (board task chxw5cJrcarpNLY2HrDg).
// UbeLayout self-gates on the `ube-bar-prep` flag (FlagGate → Navigate to "/").
const UbeLayout = lazy(() => import('@/components/ube/UbeLayout').then(module => ({ default: module.UbeLayout })));
const UbeProgressPage = lazy(() => import('@/pages/ube/UbeProgressPage').then(module => ({ default: module.UbeProgressPage })));
const UbeComingSoonPage = lazy(() => import('@/pages/ube/UbeComingSoonPage').then(module => ({ default: module.UbeComingSoonPage })));
// /ube/coach — MEE essay practice-prompt picker host (board Q016kyzpVShp47yrCrR7).
// Browse surface: visible to all flag-on users; the submit path is the entitlement
// leaf (free-quota + UbeContentGate, board 0UUgu5hQ), not a hard gate here.
const UbeCoachPage = lazy(() => import('@/pages/ube/UbeCoachPage').then(module => ({ default: module.UbeCoachPage })));
// UBE browse-mode paywall (board 87q2TNhDml6gO9d2BTp8). The four functional
// /ube/* pages are browseable by everyone (flag-gated only) — unentitled users
// see the analytics with a <UbeBrowseModeCallout> nudge, not a redirect.
// UbeContentGate (src/components/ube/UbeContentGate.tsx) is intentionally NOT
// imported here: it is the hard-redirect gate reserved for FUTURE Legacy
// interaction-leaf routes (take a question beyond free quota, run a full sim,
// deep coach), mirroring how NextGenContentGate wraps only act-on-content
// leaves. UbePricingPage is the public sales page the callout CTA links to.
const UbePricingPage = lazy(() => import('@/pages/ube/UbePricingPage').then(module => ({ default: module.UbePricingPage })));

// Import case brief pages
const CaseBriefPage = lazy(() => import('@/pages/case-briefs/CaseBriefPage'));



// Import Quiz Mode pages (SHELVED - not working well, may be restored later)
// const QuizSetup = lazy(() => import('@/pages/quiz/QuizSetup'));
// const QuizMode = lazy(() => import('@/pages/quiz/QuizMode'));

// Issue Spotter and Essay Coach - enabled for dev, disabled in production
const IssueSpotterPageWrapper = lazy(() => import('@/components/issue-spotter/IssueSpotterPageWrapper'));
const EssayCoachPageWrapper = lazy(() => import('@/components/essay-coach/EssayCoachPageWrapper'));

// Practice landing page (gateway to Issue Spotter + Essay Coach)
const PracticePage = lazy(() => import('@/pages/PracticePage'));

// Import notes page
const NotesPage = lazy(() => import('@/pages/NotesPageWrapper'));

// Import community page
const CommunityPage = lazy(() => import('@/components/community/CommunityPageWrapper'));
const GroupInviteLanding = lazy(() => import('@/components/community/GroupInviteLanding').then(m => ({ default: m.GroupInviteLanding })));

// Flag-gated wrapper for /community/groups/join/:code — the invite landing is a
// study-group entry point, so it needs BOTH community-board (umbrella) AND
// community-study-groups (sub-feature) to be ON.
//
// MUST wait for flagsReady before deciding: useFlag returns false while the
// Unleash client is still fetching, and this route is an external entry point
// hit by FIRST-TIME visitors (invite recipients) with no localStorage flag
// cache — redirecting on the initial false reading sent every invitee to
// /dashboard before the flags arrived. Returning users never saw it because
// their cached flags resolve true instantly.
function FlagGatedGroupInvite() {
  const boardEnabled = useFlag('community-board');
  const studyGroupsEnabled = useFlag('community-study-groups');
  const { flagsReady, flagsError } = useFlagsStatus();

  // SDK still fetching (and not errored) — hold with a spinner, don't decide yet.
  if (unleashEnabled && !flagsReady && !flagsError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!boardEnabled || !studyGroupsEnabled) {
    return <Navigate to="/dashboard" replace />;
  }
  return <GroupInviteLanding />;
}

// Import cases pages
const CasesSearchPage = lazy(() => import('@/pages/cases/CasesSearchPage'));
const CaseDetailPage = lazy(() => import('@/pages/cases/CaseDetailPage'));

// Import the checkout confirmation page
const CheckoutConfirmationPage = lazy(() => import('@/pages/CheckoutConfirmationPage').then(module => ({ default: module.CheckoutConfirmationPage })));

// Public cancellation-feedback survey — token-authenticated, NO auth required
// (the ?token= in the email link is the credential). Standalone one-page form,
// registered OUTSIDE ProtectedRoute / PersistentLayout. Board ODItTB29ohrpwAgU2gUf.
const CancelFeedbackPage = lazy(() => import('@/pages/CancelFeedbackPage'));

// Import the pricing page
const PricingPage = lazy(() => import('@/pages/PricingPage').then(module => ({ default: module.PricingPage })));

// Import feature landing pages
const OutlinesFeaturePage = lazy(() => import('@/pages/features/OutlinesFeaturePage'));
const SubjectPreviewPage = lazy(() => import('@/pages/outlines/SubjectPreviewPage'));

// Stage/product marketing pages — /law-school/* and /bar/*.
// Deliberately NOT under /nextgen/*: that namespace is the authenticated app
// (/nextgen/progress, /nextgen/skills, /nextgen/official-questions, ...), so a
// marketing page there would collide with a gated app route of the same name.
// Placeholders today; content is owned by sibling board tasks.
const LawSchoolStagePage = lazy(() => import('@/pages/marketing/LawSchoolStagePage'));
const LawSchoolUnlimitedPage = lazy(() => import('@/pages/marketing/LawSchoolUnlimitedPage'));
const BarStagePage = lazy(() => import('@/pages/marketing/BarStagePage'));
const BarPracticePage = lazy(() => import('@/pages/marketing/BarPracticePage'));
// Real content, not a placeholder — ChatGPT Ads landing page, prerendered.
const BarPracticeTrialPage = lazy(() => import('@/pages/marketing/BarPracticeTrialPage'));
const BarOfficialQuestionsPage = lazy(() => import('@/pages/marketing/BarOfficialQuestionsPage'));
const BarCoursePage = lazy(() => import('@/pages/marketing/BarCoursePage'));
// /lp/* — ads-only namespace. Prerendered AND noindex; see the route below.
const NextGenPracticeLpAPage = lazy(() => import('@/pages/marketing/NextGenPracticeLpAPage'));
// HomeVariantBPage now serves `/` and is imported statically at the top of this
// file — see the comment on that import for why it is not lazy.

// Import AuthCallback
import AuthCallback from './pages/auth/AuthCallback';

// Admin "view as user" impersonation landing page (signs in from a custom token).
import ImpersonateCallbackPage from './pages/auth/ImpersonateCallbackPage';
import { ImpersonationBanner } from '@/components/impersonation/ImpersonationBanner';

// Import Roadmap page
const Roadmap = lazy(() => import('@/pages/Roadmap').then(module => ({ default: module.Roadmap })));
import ResendConfirmationPage from './pages/auth/ResendConfirmationPage';

// Import NotFoundPage for 404 handling
const NotFoundPage = lazy(() => import('@/pages/common/NotFoundPage'));

// Import sidebar context from separate file to avoid circular dependencies
import { SidebarContext } from '@/contexts/SidebarContext';

// Create thread context
export type SelectedThreadContextType = {
  selectedThreadId: string | null;
  setSelectedThreadId: (threadId: string | null) => void;
};

export const SelectedThreadContext = createContext<SelectedThreadContextType>({
  selectedThreadId: null,
  setSelectedThreadId: () => {}
});

// Add a simple redirect component that doesn't trigger authentication initialization
const SimpleRedirect = ({ to }: { to: string }) => {
  return <Navigate to={to} replace />;
};

// Redirect that preserves :taskId param from old /nextgen/practice/:taskId URLs
const PracticeTaskRedirect = () => {
  const { taskId } = useParams();
  return <Navigate to={`/nextgen/skills/${taskId}`} replace />;
};

// Thin wrappers for NextGen coursework using shared components
const NextGenCourseworkLibrary = () => (
  <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">
    <CourseLibrary courseType="nextgen" basePath="/nextgen/coursework" />
  </div>
);

const NextGenCourseworkPlayer = () => (
  <CoursePlayer basePath="/nextgen/coursework" courseType="nextgen" />
);


// Create a generic async wrapper for lazy-loaded components
const createAsyncWrapper = (Component: React.ComponentType, fallbackMessage: string = "Loading...") => {
  const AsyncWrapper = function AsyncWrapper() {
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      // Use a small delay to ensure the route transition completes first
      const timeoutId = setTimeout(() => {
        startTransition(() => {
          setIsReady(true);
        });
      }, 50);

      return () => clearTimeout(timeoutId);
    }, []);

    if (!isReady) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
          <div className="flex flex-col items-center">
            <LoadingSpinner className="w-8 h-8" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">{fallbackMessage}</p>
          </div>
        </div>
      );
    }

    return (
      <Suspense fallback={
        <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
          <div className="flex flex-col items-center">
            <LoadingSpinner className="w-8 h-8" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">{fallbackMessage}</p>
          </div>
        </div>
      }>
        <Component />
      </Suspense>
    );
  };
  AsyncWrapper.displayName = `AsyncWrapper(${Component.displayName || Component.name || 'Component'})`;
  return AsyncWrapper;
};


// Create async wrappers for components that may cause suspense issues
const AsyncAuthPage = createAsyncWrapper(AuthPage, "Loading authentication...");
const AsyncCheckoutConfirmationPage = createAsyncWrapper(CheckoutConfirmationPage, "Loading confirmation...");
const AsyncPricingPage = createAsyncWrapper(PricingPage, "Loading pricing...");

import { AdminGuardedRoute } from './components/guards/AdminGuardedRoute';
import { AdminOnlyRoute } from './components/guards/AdminOnlyRoute';
import { StudicataGuardedRoute } from './components/guards/StudicataGuardedRoute';

// Kill-switch fallback for the school domain. On school.jdsimplified.com the
// Professor Console IS the root route, so FacultyLayout's own flag fallback
// (`Navigate to "/"`) would loop back into the console. When the
// `faculty-console` flag is OFF on this domain, exit to the main site instead.
function SchoolFlagOffRedirect() {
  useEffect(() => {
    window.location.replace('https://jdsimplified.com/');
  }, []);
  return null;
}

// R22 share links: Canvas/TWEN posts carry the school-branded URL
// (school.jdsimplified.com/a/{slug}), but student surfaces never live on the
// school domain (spec §3.2) — bounce to the main app BEFORE any auth wall so
// the student signs in where the assignment actually lives.
function SchoolShareRedirect() {
  const { slug } = useParams<{ slug: string }>();
  useEffect(() => {
    // Strip the school. subdomain of WHATEVER host we're on (prod today, a
    // school-dev twin tomorrow) so the bounce stays in-environment; fall back
    // to prod only when the host doesn't match the pattern.
    const { hostname, protocol } = window.location;
    const target = hostname.startsWith('school.')
      ? `${protocol}//${hostname.slice('school.'.length)}`
      : 'https://jdsimplified.com';
    window.location.replace(`${target}/a/${encodeURIComponent(slug ?? '')}`);
  }, [slug]);
  return null;
}

// Create router with domain-aware routes
function AppRoutes() {
  const { isJDSimplified, isAskJDS, isAdmin, isStudicata, isSchool } = useDomain();

  logger.debug('Rendering routes', { isJDSimplified, isAdmin, isStudicata, isSchool });

  // school.jdsimplified.com — Professor Console (314 Formative Assessment Kit).
  // Faculty-only surface (spec D7 + §7.1: domain is school. — SINGULAR): mounts
  // the EXISTING /faculty/* route tree at the domain root, so both `/` and
  // `/faculty` resolve to the console and main-app deep links keep working.
  // Same ProtectedRoute + FacultyLayout wrappers as the main-app /faculty/*
  // block; FacultyLayout still self-gates on the `faculty-console` flag (the
  // kill switch) — the outer FlagGate here only supplies a domain-safe OFF
  // fallback (see SchoolFlagOffRedirect). Student surfaces (consent, gated
  // result) stay on jdsimplified.com — never on this domain (spec §3.2).
  if (isSchool) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/auth" element={<AsyncAuthPage />} />
          {/* R22: pre-auth origin bounce — student landing lives on the main app. */}
          <Route path="/a/:slug" element={<SchoolShareRedirect />} />
          {[
            { path: '/', El: FacultyClassesPage },
            { path: '/faculty', El: FacultyClassesPage },
            { path: '/faculty/classes/new', El: FacultyClassSetupPage },
            { path: '/faculty/classes/:classId/settings', El: FacultyClassSetupPage },
            { path: '/faculty/classes/:classId/assignments/new', El: FacultyAssignmentBuilderPage },
            { path: '/faculty/classes/:classId/outcome-mapping', El: FacultyOutcomeMappingPage },
            { path: '/faculty/classes/:classId/analytics', El: FacultyAnalyticsPage },
            { path: '/faculty/classes/:classId/report', El: FacultyCompliancePage },
            { path: '/faculty/assignments/:assignmentId/edit', El: FacultyAssignmentBuilderPage },
            { path: '/faculty/assignments/:assignmentId/queue', El: FacultyReviewQueuePage },
            { path: '/faculty/assignments/:assignmentId/remediation', El: FacultyRemediationPage },
            { path: '/faculty/reviews/:submissionId', El: FacultyReviewPage },
            { path: '/faculty/desk', El: FacultyDeskPage },
            { path: '/faculty/classes/:classId/assignments', El: FacultyAssignmentsPage },
            { path: '/faculty/classes/:classId/students', El: FacultyClassStudentsPage },
            // More specific than the roster path above, so React Router ranks it
            // first and neither shadows the other (task TaluPBCQhKUwXT1MazDJ).
            { path: '/faculty/classes/:classId/students/:userId', El: FacultyStudentProfilePage },
            { path: '/faculty/classes/:classId/materials', El: FacultyMaterialsPage },
            { path: '/faculty/classes/:classId/review', El: FacultyReviewInboxPage },
            { path: '/faculty/classes/:classId/discussion', El: FacultyClassDiscussionPage },
            { path: '/faculty/classes/:classId/discussion/:threadId', El: FacultyClassDiscussionPage },
            { path: '/faculty/room', El: FacultyRoomPage },
            { path: '/faculty/room/:threadId', El: FacultyRoomPage },
            { path: '/faculty/templates', El: FacultyTemplatesPage },
            { path: '/faculty/templates/from-syllabus', El: FacultyTemplateFromSyllabusPage },
            { path: '/faculty/reports', El: FacultyProgramReportsPage },
            { path: '/faculty/templates/:templateId', El: FacultyTemplateDetailPage },
            { path: '/faculty/templates/:templateId/deploy', El: FacultyTemplateDeployPage },
            { path: '/faculty/templates/:templateId/reports/:reportId', El: FacultyCurrencyReportPage },
          ].map(({ path, El }) => (
            <Route key={path} path={path} element={
              <FlagGate flag="faculty-console" fallback={<SchoolFlagOffRedirect />}>
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader message="Loading Professor Console..." />}>
                    <FacultyLayout><El /></FacultyLayout>
                  </Suspense>
                </ProtectedRoute>
              </FlagGate>
            } />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    );
  }

  // Studicata partner portal — NextGen only, no community, no homepage
  // Access restricted to is_admin=true OR role='partner' via StudicataGuardedRoute
  // Uses layout route pattern (like admin) to avoid React Error #426 from sync Navigate during route transitions
  if (isStudicata) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Navigate to="/admin/nextgen-sales" replace />} />
          <Route path="/auth" element={<AsyncAuthPage />} />
          <Route path="/nextgen" element={<Navigate to="/nextgen/skills/practice" replace />} />

          {/* Studicata guarded routes — PersistentLayout provides sidebar */}
          <Route element={
            <StudicataGuardedRoute>
              <Suspense fallback={<PageLoader message="Loading..." />}>
                <PersistentLayout />
              </Suspense>
            </StudicataGuardedRoute>
          }>
          <Route path="/nextgen/dashboard" element={
            <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
              <NextGenLayout><NextGenDashboardPage /></NextGenLayout>
            </Suspense>
          } />
          <Route path="/nextgen/coursework" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkLibrary /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId/:moduleId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/skills/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/drills" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/library" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
          <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
          <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/skills/:taskId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenTaskDetail /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/simulations" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimulationsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sectionNumber" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sessionId/review" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimReviewPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/exam/:runId" element={
            <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
          } />
          <Route path="/nextgen/exam/:runId/results" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
          <Route path="/nextgen/progress/overview" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/readiness" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/insights" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQPracticePage /></NextGenLayout></Suspense>
          } />
          {/* Old workspace routes retired — redirect to practice system */}
          <Route path="/nextgen/workbench/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/client-chat/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/grade/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coach" element={
            <Suspense fallback={<PageLoader />}><NextGenCoachPage /></Suspense>
          } />
          <Route path="/nextgen/exam-map" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamMapPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/mcq" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><MCQPracticeWithReset /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/mcq/:questionId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQSinglePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sets/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenSetRedirect /></Suspense>
          } />
          {/* Legacy redirect — keep for backward compat */}
          <Route path="/nextgen/mcq-practice" element={<Navigate to="/nextgen/practice/mcq" replace />} />
          <Route path="/nextgen/practice/iqs" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenIQSPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/iqs/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenIQSPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/pt" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenPTPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/pt/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenPTPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/drafting" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice/drafting/:setId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout></Suspense>
          } />
          {/* /nextgen/practice/counseling/:taskId route retired 2026-05-25 (board
              RYu91USgyqQZ3W7gj4qi) — client_chat task type has zero rows. */}
          <Route path="/nextgen/practice/counseling/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coursework/:courseId/:moduleId/:lessonId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />

          {/* Standard JDS user routes — so sidebar links work on studicata domain */}
          <Route path="/chat/:threadId?" element={
            <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
            </FlagGate>
          } />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/dashboard/*" element={
            <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
              <NavbarProvider><IntelligenceDashboard /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/flashcards/*" element={
            <FlagGate flag="flashcards" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
                <NavbarProvider><FlashcardsPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines" element={<Navigate to="/outlines/library" replace />} />
          <Route path="/outlines/library" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline library..." />}>
                <NavbarProvider><OutlineLibrary /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analyses" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading your analyses..." />}>
                <NavbarProvider><OutlineAnalyses /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/upload" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
                <NavbarProvider><UploadOutlinePage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analysis/:id" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
                <NavbarProvider><OutlineAnalysisDetail /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/archive" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline archive..." />}>
                <NavbarProvider><OutlineArchive /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/cases" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading cases..." />}><CasesSearchPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/:id" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case details..." />}><CaseDetailPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/collections/*" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case collections..." />}><CaseBriefPage /></Suspense>
            </FlagGate>
          } />
          {/* Legacy collection URLs — parity with the admin and JDS blocks. */}
          <Route path="/case-collections" element={<Navigate to="/cases/collections" replace />} />
          <Route path="/case-collections/*" element={<Navigate to="/cases/collections" replace />} />
          <Route path="/courses/*" element={
            <FlagGate flag="courses" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading courses..." />}>
                <NavbarProvider><CoursesPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/notes" element={
            <FlagGate flag="notes" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading notes..." />}><NotesPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/practice" element={
            <Suspense fallback={<PageLoader message="Loading practice..." />}>
              <NavbarProvider><PracticePage /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/issue-spotter/*" element={
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider><IssueSpotterPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/essay-coach/*" element={
            <FlagGate flag="essay-coach" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider><EssayCoachPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          {/* NextGen sales — same component as admin; StudicataGuardedRoute (admin OR
              partner) is the gate. Backend omits recentPurchases (PII) for partners. */}
          <Route path="/admin/nextgen-sales" element={
            <Suspense fallback={<PageLoader message="Loading NextGen sales..." />}>
              <NextGenSalesPage />
            </Suspense>
          } />
          </Route>
          <Route path="*" element={<Navigate to="/admin/nextgen-sales" replace />} />
        </Routes>
      </>
    );
  }

  // Admin domain routes — wrapped in PersistentLayout for shared sidebar
  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Routes>
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

        <Route path="/auth" element={<AsyncAuthPage />} />

        {/* All admin + user-facing routes wrapped in PersistentLayout */}
        <Route element={
          <AdminGuardedRoute>
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <PersistentLayout />
            </Suspense>
          </AdminGuardedRoute>
        }>
          {/* Admin-specific pages */}
          <Route path="/admin/dashboard" element={<Suspense fallback={<PageLoader message="Loading admin dashboard..." />}><AdminDashboard /></Suspense>} />
          {/* NextGen sales — PII (user emails) gated to is_admin=true via AdminOnlyRoute */}
          <Route path="/admin/nextgen-sales" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading NextGen sales..." />}><NextGenSalesPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/users" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading user management..." />}><AdminUsers /></Suspense></AdminOnlyRoute>} />
          {/* User 360 CRM detail — PII gated to is_admin=true via AdminOnlyRoute */}
          <Route path="/admin/users/:id" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading user detail..." />}><AdminUserDetailPage /></Suspense></AdminOnlyRoute>} />
          {/* Relationship Hub (internal CRM) — admin-only (school_outreach & friends are is_admin() RLS). */}
          <Route path="/admin/schools" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading pipeline..." />}><SchoolPipelinePage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/schools/reports" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading reports..." />}><CrmReportsPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/schools/:schoolId" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading school..." />}><SchoolHubPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/tasks" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading tasks..." />}><AdminTasksPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/tasks/:taskId" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading task..." />}><TaskDetailPage /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/analytics" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading web analytics..." />}><AdminWebAnalytics /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/error-logs" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading error logs..." />}><AdminErrorLogs /></Suspense></AdminOnlyRoute>} />
          {/* JDS Features — consolidated content-CRUD host (Courses overview / Flashcards / Outlines / Case Law).
              Staff-accessible (gated by the wrapping AdminGuardedRoute), matching the sidebar 'staff' visibility. */}
          <Route path="/admin/jds-features" element={<Suspense fallback={<PageLoader message="Loading JDS features..." />}><JdsFeaturesPage /></Suspense>} />
          {/* Legacy list routes → redirect into the consolidated host's matching tab. */}
          <Route path="/admin/courses" element={<Navigate to="/admin/jds-features?tab=courses" replace />} />
          {/* Heavy CourseDetail stays its own standalone route — NOT a tab. */}
          <Route path="/admin/courses/:courseId" element={<Suspense fallback={<PageLoader message="Loading course details..." />}><LazyAdminCourseDetail /></Suspense>} />
          <Route path="/admin/flashcards" element={<Navigate to="/admin/jds-features?tab=flashcards" replace />} />
          <Route path="/admin/outlines" element={<Navigate to="/admin/jds-features?tab=outlines" replace />} />
          <Route path="/admin/cases" element={<Navigate to="/admin/jds-features?tab=cases" replace />} />
          <Route path="/admin/blog" element={<Suspense fallback={<PageLoader message="Loading blog management..." />}><AdminBlogManagement /></Suspense>} />
          <Route path="/admin/blog/generate" element={<Suspense fallback={<PageLoader message="Loading AI generator..." />}><BlogAIGenerator /></Suspense>} />
          <Route path="/admin/settings" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading pricing..." />}><AdminPricing /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/content-health" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading content health..." />}><AdminContentHealth /></Suspense></AdminOnlyRoute>} />
          {/* Content Pipeline — staff-accessible (relies on the domain-level AdminGuardedRoute,
              matching lesson_transcripts staff_full_transcript_access RLS: admin + content_manager). */}
          <Route path="/admin/content-pipeline" element={<Suspense fallback={<PageLoader message="Loading content pipeline..." />}><AdminContentPipeline /></Suspense>} />
          <Route path="/admin/content-pipeline/transcript/:assetId" element={<Suspense fallback={<PageLoader message="Loading transcript..." />}><AdminTranscriptReview /></Suspense>} />
          {/* Studicata QA — staff-accessible (relies on the domain-level AdminGuardedRoute,
              matching studicata_transcripts staff_full_studicata_transcript_access RLS: admin + content_manager). */}
          <Route path="/admin/studicata-qa" element={<Suspense fallback={<PageLoader message="Loading Studicata QA..." />}><AdminStudicataQa /></Suspense>} />
          <Route path="/admin/studicata-qa/:videoKey" element={<Suspense fallback={<PageLoader message="Loading transcript..." />}><AdminStudicataReview /></Suspense>} />
          <Route path="/admin/moderation" element={<Suspense fallback={<PageLoader message="Loading community moderation..." />}><AdminCommunityModeration /></Suspense>} />
          {/* R28 faculty case flags — JDS staff only (server-enforced 403). */}
          <Route path="/admin/case-flags" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading faculty flags..." />}><AdminCaseFlags /></Suspense></AdminOnlyRoute>} />
          <Route path="/admin/support" element={<Suspense fallback={<PageLoader message="Loading support queue..." />}><AdminSupport /></Suspense>} />
          <Route path="/admin/support/:id" element={<Suspense fallback={<PageLoader message="Loading support thread..." />}><AdminSupport /></Suspense>} />
          <Route path="/admin/set-admin" element={<AdminOnlyRoute><Suspense fallback={<PageLoader message="Loading admin setup..." />}><SetAdminStatus /></Suspense></AdminOnlyRoute>} />

          {/* User-facing pages accessible from admin sidebar */}
          <Route path="/chat/:threadId?" element={
            <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
            </FlagGate>
          } />
          <Route path="/notes" element={
            <FlagGate flag="notes" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading notes..." />}><NotesPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/dashboard/*" element={
            <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
              <NavbarProvider><IntelligenceDashboard /></NavbarProvider>
            </Suspense>
          } />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/flashcards/*" element={
            <FlagGate flag="flashcards" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
                <NavbarProvider><FlashcardsPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/issue-spotter/*" element={
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider><IssueSpotterPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/essay-coach/*" element={
            <FlagGate flag="essay-coach" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider><EssayCoachPageWrapper /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines" element={<Navigate to="/outlines/library" replace />} />
          <Route path="/outlines/library" element={
            <FlagGate flag="outlines" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline library..." />}>
                <NavbarProvider><OutlineLibrary /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analyses" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading analyses..." />}>
                <NavbarProvider><OutlineAnalyses /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/upload" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
                <NavbarProvider><UploadOutlinePage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/outlines/analysis/:id" element={
            <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
              <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
                <NavbarProvider><OutlineAnalysisDetail /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/cases" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading cases..." />}><CasesSearchPage /></Suspense>
            </FlagGate>
          } />
          <Route path="/cases/:id" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case details..." />}><CaseDetailPage /></Suspense>
            </FlagGate>
          } />
          {/* Case Law Comparatives (collections) — MUST be registered here, not only in
              the JDS block. Without it `/cases/collections` falls through to
              `/cases/:id` with id="collections" on admin.jdsimplified.com and
              PostgREST rejects the uuid cast with 22P02. Founder fix 2026-09-11. */}
          <Route path="/cases/collections/*" element={
            <FlagGate flag="case-brief-library" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading case collections..." />}><CaseBriefPage /></Suspense>
            </FlagGate>
          } />
          {/* Legacy collection URLs — parity with the JDS block. */}
          <Route path="/case-collections" element={<Navigate to="/cases/collections" replace />} />
          <Route path="/case-collections/*" element={<Navigate to="/cases/collections" replace />} />
          <Route path="/courses/*" element={
            <FlagGate flag="courses" fallback={<Navigate to="/" replace />}>
              <Suspense fallback={<PageLoader message="Loading courses..." />}>
                <NavbarProvider><CoursesPage /></NavbarProvider>
              </Suspense>
            </FlagGate>
          } />
          <Route path="/community/*" element={
            <Suspense fallback={<PageLoader message="Loading community..." />}>
              <NavbarProvider><CommunityPage /></NavbarProvider>
            </Suspense>
          } />

          {/* NextGen routes — accessible from admin domain for testing */}
          <Route path="/nextgen" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/dashboard" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenDashboardPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkLibrary /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/coursework/:courseId/:moduleId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenCourseworkPlayer /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
          <Route path="/nextgen/skills/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/drills" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/library" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSkillsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
          <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
          <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/skills/:taskId" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenTaskDetail /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/simulations" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimulationsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sectionNumber" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/sim/:sessionId/review" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSimReviewPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/exam/:runId" element={
            <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
          } />
          <Route path="/nextgen/exam/:runId/results" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
          <Route path="/nextgen/progress/overview" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/readiness" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/progress/insights" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenProgressPage /></NextGenLayout></Suspense>
          } />
          <Route path="/nextgen/practice" element={
            <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQPracticePage /></NextGenLayout></Suspense>
          } />
          {/* Old workspace routes retired — redirect to practice system */}
          <Route path="/nextgen/workbench/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/client-chat/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/grade/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
          <Route path="/nextgen/coach" element={
            <Suspense fallback={<PageLoader />}><NextGenCoachPage /></Suspense>
          } />
        </Route>

        {/* Catch-all 404 route - must be last */}
        <Route path="*" element={
          <Suspense fallback={<PageLoader message="Loading..." />}>
            <NotFoundPage />
          </Suspense>
        } />
      </Routes>
      </>
    );
  }
  
  // Regular routes for Ask JDS and JD Simplified domains
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* THE CUTOVER (2026-09-11, board 1MsSnZAnseQ4qBbvPUpv). `/` was
          src/components/HomePage.tsx, which was itself a PRODUCT page selling
          law-school features. It is now the variant-B router: five product tiles
          across two stages, with the product content it used to carry moved to
          /law-school/unlimited and /law-school.

          HomePage.tsx is DELETED rather than left unrouted. A component with
          zero importers renders nothing, is invisible to every gate, and reads
          as live to the next person who opens it
          (.claude/rules/silent-degradation.md §1) — and this one owned a
          FAQPage JSON-LD injection and a scroll-spy contract, so leaving it
          would have meant two plausible sources of truth for `/`. */}
      <Route path="/" element={<HomeVariantBPage />} />

      {/* Proposed TLD hub — unlisted, noindex. Live `/` is unchanged.
          Brief: workbench/homepage-redesign/design-brief.md */}
      <Route path="/grok" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokHubPage />
        </Suspense>
      } />
      <Route path="/grok-combined" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokCombinedPage />
        </Suspense>
      } />
      <Route path="/grok-sub" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokSubPage />
        </Suspense>
      } />
      <Route path="/grok-law-school" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokLawSchoolPage />
        </Suspense>
      } />
      <Route path="/grok-jds-unlimited" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokJdsUnlimitedPage />
        </Suspense>
      } />
      <Route path="/grok-bar-practice" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokBarPracticePage />
        </Suspense>
      } />
      <Route path="/grok-bar-official-questions" element={
        <FlagGate
          flag="nextgen-official-questions"
          pending={<PageLoader message="Loading..." />}
          fallback={<SimpleRedirect to="/grok-combined" />}
        >
          <Suspense fallback={<PageLoader message="Loading..." />}>
            <GrokBarOfficialQuestionsPage />
          </Suspense>
        </FlagGate>
      } />
      <Route path="/grok-nextgen-course" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <GrokNextgenCoursePage />
        </Suspense>
      } />
      
      {/* Add back the login redirect without triggering auth initialization */}
      <Route path="/login" element={<SimpleRedirect to="/auth" />} />
      
      <Route path="/auth" element={
        <AsyncAuthPage />
      } />
      
      {/* Auth callback route for email confirmation */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin "view as user" landing — public route (user isn't signed in yet
          when the admin opens the link). Signs in from the custom token in the
          URL fragment, then redirects to /chat. Outside ProtectedRoute + layout. */}
      <Route path="/impersonate-callback" element={<ImpersonateCallbackPage />} />

      {/* Resend confirmation email route */}
      <Route path="/auth/resend-confirmation" element={<ResendConfirmationPage />} />

      {/* Password reset routes */}
      <Route path="/auth/reset-password" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <ForgotPasswordPage />
        </Suspense>
      } />
      <Route path="/auth/update-password" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <ResetPasswordPage />
        </Suspense>
      } />

      {/* Legal Pages - Public Access (Lazy-loaded) */}
      <Route path="/terms" element={<Suspense fallback={<PageLoader message="Loading terms..." />}><TermsOfService /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<PageLoader message="Loading privacy policy..." />}><PrivacyPolicy /></Suspense>} />
      <Route path="/disclaimer" element={<Suspense fallback={<PageLoader message="Loading disclaimer..." />}><Disclaimer /></Suspense>} />
      
      {/* Feature Landing Pages - Public Access */}
      <Route path="/features/outlines" element={
        <Suspense fallback={<PageLoader message="Loading outlines..." />}>
          <OutlinesFeaturePage />
        </Suspense>
      } />

      {/* Subject Preview Pages - Public SEO pages */}
      <Route path="/outlines/:slug" element={
        <Suspense fallback={<PageLoader message="Loading outline..." />}>
          <SubjectPreviewPage />
        </Suspense>
      } />

      {/* Debug Routes - Development Only */}
      {import.meta.env.DEV && (
        <Route 
          path="/debug/usermaven" 
          element={
            <Suspense fallback={<PageLoader message="Loading debug..." />}>
              <UsermavenDebug />
            </Suspense>
          } 
        />
      )}
      
      {/* Pricing Page — Pure Client v1: no purchase UI in the native app.
          Native users are redirected home; web is unchanged. */}
      <Route path="/pricing" element={
        <ProtectedRoute>
          <NativePurchaseGuard>
            <AsyncPricingPage />
          </NativePurchaseGuard>
        </ProtectedRoute>
      } />
      
      {/* /unlimited deprecated 2026-05-11 — redirect to canonical /pricing.
          Catches any cached browser tabs / external bookmarks. */}
      <Route path="/unlimited" element={<Navigate to="/pricing" replace />} />
      
      {/* Checkout Confirmation Page (Standalone). Post-purchase result screen —
          native users have no web-checkout entry point, so a deep-link here is
          redirected home rather than showing checkout-adjacent chrome. */}
      <Route path="/checkout-confirmation" element={
        <NativePurchaseGuard>
          <AsyncCheckoutConfirmationPage />
        </NativePurchaseGuard>
      } />

      {/* Cancellation feedback survey (Standalone, PUBLIC — token in ?token= is
          the credential). NO auth guard, NO PersistentLayout — a churning user
          arrives from an email likely logged out. Board ODItTB29ohrpwAgU2gUf. */}
      <Route path="/cancel-feedback" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <CancelFeedbackPage />
        </Suspense>
      } />

      {/* Study Group Invite Landing — public route (works for unauth users).
          Flag-gated by community-board + community-study-groups. */}
      <Route path="/community/groups/join/:code" element={
        <Suspense fallback={<PageLoader message="Loading invite..." />}>
          <FlagGatedGroupInvite />
        </Suspense>
      } />


      {/* NextGen public routes — accessible without login (marketing/upgrade pages).
          Available on dev (askjds) and live (jdsimplified) — soft-launch enables
          direct-URL access on prod even though sidebar nav is role-gated.
          F.10: route elements wrapped in `<FlagGate flag="nextgen-access">`.
          Domain hard rule `(isAskJDS || isJDSimplified)` retained — flag is
          layered ON TOP. */}
      {(isAskJDS || isJDSimplified) && (
        <>
          {/* ---------------------------------------------------------------
              Stage/product marketing namespace — /law-school/* and /bar/*.

              Same `(isAskJDS || isJDSimplified)` domain gate as the NextGen
              public routes below, but deliberately NOT wrapped in
              `<FlagGate flag="nextgen-access">` — these are stage/product
              pages, not NextGen surfaces. The block header comment above
              describes the NextGen routes only; it does not apply here.

              Bare /law-school and /bar are REAL PAGES, not redirects.

              /nextgen-prep -> /bar/practice is a SERVER-SIDE 301 in the LB
              url map (infra/b1-url-map-live.yaml), NOT a client-side
              <Navigate>: a soft redirect reads as a soft-404 to Google and
              that path carried live paid-ad spend. It is deliberately absent
              from this router. Re-adding a route for that path would defeat
              the redirect on every in-app client navigation — React Router
              matches it locally and the load balancer never sees a request —
              so any REMAINING in-app link to it is a bug: repoint the link,
              do not re-add the route. (Written without the literal JSX, on
              purpose: scripts/check-prerender-coverage.ts scans raw source and
              cannot tell a route from a comment about one, so the phrase
              spelled out here would red the build. That checker fails CLOSED,
              which is the right direction, but it is worth knowing.)
              --------------------------------------------------------------- */}
          <Route path="/law-school" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <LawSchoolStagePage />
            </Suspense>
          } />
          <Route path="/law-school/unlimited" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <LawSchoolUnlimitedPage />
            </Suspense>
          } />
          <Route path="/bar" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <BarStagePage />
            </Suspense>
          } />
          <Route path="/bar/practice" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <BarPracticePage />
            </Suspense>
          } />
          {/* Free-trial ad landing page. UNLIKE its /bar/* siblings this is real
              content, is in SEO_ROUTES, and IS prerendered — OAI-AdsBot has to
              read the headline/benefits/terms/FAQ from the initial HTML. Do not
              add it to INTENTIONALLY_NOT_PRERENDERED. */}
          <Route path="/bar/practice/trial" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <BarPracticeTrialPage />
            </Suspense>
          } />

          {/* Official NCBE Question Packs — marketing page for LICENSED content,
              and therefore gated on the kill switch rather than merely on the
              domain. The `nextgen-official-questions` flag IS the atomic kill
              switch (.claude/rules/ncbe-licensed-content.md §III.C/§III.F): OFF
              means every licensed surface hard-disables, server-side, alongside
              bulk entitlement revocation — so a marketing page still selling the
              product while the switch is pulled would advertise something the
              platform is contractually removing.

              Verified 2026-09-07 via the Unleash admin API: development ON,
              production OFF. Prod visitors therefore land on /bar today, which
              is correct — checkout is still stubbed (T6).

              Falls back to /bar: this page's own parent, which still explains
              the exam and still routes to the two products that ARE available.
              /official-ncbe-questions — the public sales page for this same
              product behind this same flag — was repointed to match when
              /nextgen-prep was retired. The matching product card on
              BarStagePage is gated on the SAME flag, so the card cannot
              advertise a route that bounces the visitor back to it. */}
          <Route path="/bar/ncbe-nextgen-questions" element={
            <FlagGate flag="nextgen-official-questions" fallback={<Navigate to="/bar" replace />} pending={<PageLoader message="Loading..." />}>
              <Suspense fallback={<PageLoader message="Loading..." />}>
                <BarOfficialQuestionsPage />
              </Suspense>
            </FlagGate>
          } />
          <Route path="/bar/course" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <BarCoursePage />
            </Suspense>
          } />

          {/* ── /lp/* — the ADS-ONLY namespace ────────────────────────────────
              Like /bar/practice/trial this is real content, is in SEO_ROUTES,
              and IS prerendered: ad crawlers (including OAI-AdsBot) read the
              INITIAL HTML, so the copy must be server-rendered. Do not add it
              to INTENTIONALLY_NOT_PRERENDERED.

              UNLIKE /bar/practice/trial it is `noindex: true` in SEO_ROUTES and
              therefore excluded from sitemap.xml. It measures PAID traffic, not
              organic; public/robots.txt does not disallow /lp/, and an -a/-b
              A/B pair would otherwise be near-duplicate content competing with
              itself, with /bar/practice/trial, and with /bar/practice.

              It could NOT have lived under /auth or /signup: robots.txt
              disallows both and src/seo/routes.ts excludes the whole auth
              family, so an ad destination there is invisible to Googlebot AND
              to AdsBot — i.e. to the crawler the page exists for.

              The path string is `NEXTGEN_PRACTICE_LP_A_PATH`, exported from
              useNextGenPracticeTrialSignup and typed `PostAuthReturnTo` — a
              compile-time pin to RETURN_TO_ALLOWLIST. It stays a literal HERE
              because <Route path> takes one, but the hook, the page and the
              post-auth intent all share that constant. */}
          <Route path="/lp/nextgen-practice-a" element={
            <Suspense fallback={<PageLoader message="Loading..." />}>
              <NextGenPracticeLpAPage />
            </Suspense>
          } />

          {/* /home-preview IS GONE. It was the unlisted founder-review route for
              this page; at the cutover the CONTENT moved to `/` and the route was
              deleted rather than promoted — keeping it would have served a second,
              near-duplicate copy of the homepage at its own URL. Its entries in
              INTENTIONALLY_NOT_PRERENDERED (src/seo/routes.ts) and in the
              NEVER_PROMOTABLE registry (prerender-allowlist-ratchet.test.ts) were
              removed in the same commit; the ratchet's stale-registry check reds
              the build if one is left behind. */}

          <Route path="/nextgen" element={
            <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading NextGen Prep..." />}>
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLandingPage />
              </Suspense>
            </FlagGate>
          } />
          <Route path="/nextgen/product" element={<Navigate to="/bar/practice" replace />} />
          <Route path="/nextgen/pricing" element={
            <ProtectedRoute>
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                <NativePurchaseGuard>
                  <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                    <NextGenUpgradePage />
                  </Suspense>
                </NativePurchaseGuard>
              </FlagGate>
            </ProtectedRoute>
          } />
          {/* Backward compat — old /nextgen/upgrade path redirects to canonical /nextgen/pricing.
              Existing emails, ad creatives, and Stripe success URLs may still reference /upgrade. */}
          <Route path="/nextgen/upgrade" element={<Navigate to="/nextgen/pricing" replace />} />
          {/* Internal Official NCBE Questions purchase page. Flag-gated
              (`nextgen-official-questions`, dev=ON / prod=OFF until launch);
              falls back to /nextgen/pricing when the flag is off. */}
          <Route path="/nextgen/official-questions" element={
            <ProtectedRoute>
              <FlagGate flag="nextgen-official-questions" fallback={<Navigate to="/nextgen/pricing" replace />} pending={<PageLoader message="Loading Official NCBE Questions..." />}>
                <Suspense fallback={<PageLoader message="Loading Official NCBE Questions..." />}>
                  <NextGenOfficialQuestionsPage />
                </Suspense>
              </FlagGate>
            </ProtectedRoute>
          } />
          {/* External Official NCBE Questions sales page — PUBLIC (no auth), top
              of funnel, indexable. Flag-gated (`nextgen-official-questions`,
              dev=ON / prod=OFF until launch); falls back to /bar when off.

              /bar, NOT /bar/practice, and NOT the retired /nextgen-prep it used
              to fall back to. This page and /bar/ncbe-nextgen-questions sell the
              SAME product behind the SAME flag, so they must bounce to the same
              place — and /bar is that page's own parent, still explains the exam,
              and still routes to the two products that ARE available. Sending an
              Official-Question-Packs visitor to /bar/practice would land them on
              a DIFFERENT product's sales page, which the packs must never be
              bundled with (.claude/rules/ncbe-licensed-content.md §IV.B).

              Purchase CTAs route to the internal /nextgen/official-questions
              page, which owns the auth wall. */}
          <Route path="/official-ncbe-questions" element={
            <FlagGate flag="nextgen-official-questions" fallback={<Navigate to="/bar" replace />} pending={<PageLoader message="Loading Official NCBE Questions..." />}>
              <Suspense fallback={<PageLoader message="Loading Official NCBE Questions..." />}>
                <NextGenOfficialQuestionsSalesPage />
              </Suspense>
            </FlagGate>
          } />
          {/* /nextgen-prep AND /nextgen-prep-preview ARE GONE (2026-09-11).
              /nextgen-prep is now a server-side 301 to /bar/practice in
              infra/b1-url-map-live.yaml (see the block header above); the
              dev-only preview rendered the same component and could not outlive
              it. Both pages, NextGenPrepNavbar, their SEO_ROUTES /
              INTENTIONALLY_NOT_PRERENDERED / NEVER_PROMOTABLE entries and their
              site-navigation.json surface were deleted in that same commit. */}
        </>
      )}

      {/* Protected routes wrapped in PersistentLayout */}
      <Route element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader message="Loading..." />}>
            <PersistentLayout />
          </Suspense>
        </ProtectedRoute>
      }>
        {/* Kit student surfaces (join/consent + assignments + gated result) —
            the main-app student side of the Professor Console (spec §3.2),
            gated by the same faculty-console flag as the /faculty/* tree. */}
        <Route path="/join-class" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading..." />}><JoinClassPage /></Suspense>
          </FlagGate>
        } />
        <Route path="/assignments" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading assignments..." />}><MyClassAssignmentsPage /></Suspense>
          </FlagGate>
        } />
        <Route path="/assignments/:assignmentId" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading assignment..." />}><ClassAssignmentPage /></Suspense>
          </FlagGate>
        } />
        {/* §9 student consent settings — static segment, must precede /classes/:classId. */}
        <Route path="/classes/consent" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading consent settings..." />}><ClassConsentSettingsPage /></Suspense>
          </FlagGate>
        } />
        {/* R21 student class home + discussion (canvas 3f) — same flag gate. */}
        <Route path="/classes/:classId" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading class..." />}><ClassHomePage /></Suspense>
          </FlagGate>
        } />
        <Route path="/classes/:classId/discussion/:threadId?" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading discussion..." />}><ClassDiscussionPage /></Suspense>
          </FlagGate>
        } />
        {/* R22 share-link landing — ProtectedRoute supplies the sign-in step
            of link → sign-in → consent-if-first-time → assignment. */}
        <Route path="/a/:slug" element={
          <FlagGate flag="faculty-console" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Opening your assignment..." />}><ShareLandingPage /></Suspense>
          </FlagGate>
        } />
        <Route path="/chat/:threadId?" element={
          <FlagGate flag="ask-jds" fallback={<Navigate to="/" replace />}>
            <Suspense fallback={<PageLoader message="Loading chat..." />}><ChatContainer /></Suspense>
          </FlagGate>
        } />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="/dashboard/*" element={
          <Suspense fallback={<PageLoader message="Loading dashboard..." />}>
            <NavbarProvider>
              <IntelligenceDashboard />
            </NavbarProvider>
          </Suspense>
        } />
        {/* Courses dashboard (original Dashboard component) */}
        <Route path="/courses-dashboard" element={
          <Suspense fallback={<PageLoader message="Loading courses..." />}>
            <NavbarProvider>
              <JDSDashboard />
            </NavbarProvider>
          </Suspense>
        } />
        <Route path="/roadmap" element={
          <Suspense fallback={<PageLoader message="Loading roadmap..." />}>
            <Roadmap />
          </Suspense>
        } />
        {/* Diagnostic Assessment — flag-gated (`diagnostic-assessment`, dev=ON / prod=OFF):
            went live in prod unintentionally 2026-07-06 (Workbench sweep); dev-only per
            founder directive until the rebuild-from-question-base decision (board QTmmcrRNWe8ClhOaEKNy) */}
        <Route path="/diagnostic" element={
          <ProtectedRoute>
            <FlagGate flag="diagnostic-assessment" fallback={<Navigate to="/dashboard" replace />} pending={<PageLoader message="Loading diagnostic..." />}>
              <Suspense fallback={<PageLoader message="Loading diagnostic..." />}>
                <NavbarProvider>
                  <DiagnosticPage />
                </NavbarProvider>
              </Suspense>
            </FlagGate>
          </ProtectedRoute>
        } />
        {/* Syllabus Upload - Redirect to new dashboard location */}
        <Route path="/syllabus" element={<Navigate to="/dashboard/classes" replace />} />
        {/* Study Plan */}
        <Route path="/study-plan" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <NavbarProvider>
                <StudyPlanPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/study-plan/create" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <NavbarProvider>
                <StudyPlanPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Full plan detail now lives inside the dashboard shell — redirect the
            legacy standalone route to /dashboard/plan/:planId (Workbench IA move). */}
        <Route path="/study-plan/:planId" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading study plan..." />}>
              <StudyPlanRedirect />
            </Suspense>
          </ProtectedRoute>
        } />
        {/* Import Scores */}
        <Route path="/import-scores" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading score import..." />}>
              <NavbarProvider>
                <ImportScoresPage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/flashcards/*" element={
          <FlagGate flag="flashcards" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading flashcards..." />}>
              <NavbarProvider>
                <FlashcardsPage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />

        {/* Practice Landing Page (gateway to Issue Spotter + Essay Coach) */}
        <Route path="/practice" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading practice..." />}>
              <NavbarProvider>
                <PracticePage />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />

        {/* Issue Spotter Routes */}
        <Route path="/issue-spotter/*" element={
          <ProtectedRoute>
            <FlagGate flag="issue-spotter" fallback={<Navigate to="/dashboard" replace />}>
              <Suspense fallback={<PageLoader message="Loading issue spotter..." />}>
                <NavbarProvider>
                  <IssueSpotterPageWrapper />
                </NavbarProvider>
              </Suspense>
            </FlagGate>
          </ProtectedRoute>
        } />

        {/* Essay Coach Routes */}
        <Route path="/essay-coach/*" element={
          <ProtectedRoute>
            <FlagGate flag="essay-coach" fallback={<Navigate to="/dashboard" replace />}>
              <Suspense fallback={<PageLoader message="Loading essay coach..." />}>
                <NavbarProvider>
                  <EssayCoachPageWrapper />
                </NavbarProvider>
              </Suspense>
            </FlagGate>
          </ProtectedRoute>
        } />

        {/* Quiz Routes - SHELVED: Not working well, may be restored later */}
        {/* <Route path="/quiz/setup" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading quiz setup..." />}>
              <NavbarProvider>
                <QuizSetup />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/quiz" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading quiz..." />}>
              <NavbarProvider>
                <QuizMode />
              </NavbarProvider>
            </Suspense>
          </ProtectedRoute>
        } /> */}
        <Route path="/courses/*" element={
          <FlagGate flag="courses" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading courses..." />}>
              <NavbarProvider>
                <CoursesPage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/course-detail/:id" element={
          <Suspense fallback={<PageLoader message="Loading course details..." />}>
            <PublicCourseDetail />
          </Suspense>
        } />
        <Route path="/subscription/success" element={
          <Suspense fallback={<PageLoader message="Loading subscription details..." />}>
            <SubscriptionSuccess />
          </Suspense>
        } />
        {/* Outline routes - library is the default */}
        <Route path="/outlines" element={
          <Navigate to="/outlines/library" replace />
        } />
        <Route path="/outlines/library" element={
          <FlagGate flag="outlines" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline library..." />}>
              <NavbarProvider>
                <OutlineLibrary />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/analyses" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading your analyses..." />}>
              <NavbarProvider>
                <OutlineAnalyses />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/upload" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline upload..." />}>
              <NavbarProvider>
                <UploadOutlinePage />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/analysis/:id" element={
          <FlagGate flag="outline-iq" fallback={<Navigate to="/outlines/library" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline analysis..." />}>
              <NavbarProvider>
                <OutlineAnalysisDetail />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />
        <Route path="/outlines/archive" element={
          <FlagGate flag="outlines" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading outline archive..." />}>
              <NavbarProvider>
                <OutlineArchive />
              </NavbarProvider>
            </Suspense>
          </FlagGate>
        } />

        {/* NextGen Prep Routes — available on dev + live (prod soft-launch).
            Sidebar nav is role-gated (admin/partner only on live), but routes
            themselves are open by direct URL. Entitlement still gates feature
            use via NextGenContentGate (soft-paywall: deep routes redirect
            unentitled users to /nextgen/pricing; directory/listing surfaces
            stay browseable). */}
        {/* Public routes (/nextgen, /nextgen/product, /nextgen/pricing) are defined ABOVE the ProtectedRoute wrapper */}
        {(isAskJDS || isJDSimplified) && (
          <>
            {/* Redirects from old URLs — no entitlement check needed */}
            <Route path="/nextgen/videos" element={<Navigate to="/nextgen/coursework" replace />} />
            <Route path="/nextgen/outlines" element={<Navigate to="/nextgen/coursework" replace />} />
            <Route path="/nextgen/intelligence" element={<Navigate to="/nextgen/coach" replace />} />
            <Route path="/nextgen/practice" element={<Navigate to="/nextgen/skills/practice" replace />} />
            <Route path="/nextgen/practice/:taskId" element={<PracticeTaskRedirect />} />
            <Route path="/nextgen/coursework/outlines" element={<Navigate to="/nextgen/coursework" replace />} />

            {/* Cycle activation + purchase confirmation — auth required, NOT entitlement-gated */}
            <Route path="/nextgen/activate" element={
              <Suspense fallback={<PageLoader message="Loading..." />}>
                <NextGenLayout><NextGenActivatePage /></NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/purchase-confirmed" element={
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                {/* Post-purchase result screen — unreachable via a native checkout;
                    redirect native users home rather than show a retry-to-pricing. */}
                <NativePurchaseGuard>
                  <Suspense fallback={<PageLoader message="Loading..." />}>
                    <NextGenPurchaseConfirmedPage />
                  </Suspense>
                </NativePurchaseGuard>
              </FlagGate>
            } />

            {/* Preview route — auth required for signal attribution, NOT entitlement-gated */}
            <Route path="/nextgen/preview" element={
              <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                <Suspense fallback={<PageLoader message="Loading preview..." />}>
                  <NextGenPreviewPage />
                </Suspense>
              </FlagGate>
            } />

            {/* Public preview — Library uses nextgen_tasks_public view + counts RPC.
                Designed for unentitled users to browse the catalog before converting. */}
            <Route path="/nextgen/skills/library" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            {/* Legacy redirect */}
            <Route path="/nextgen/mcq-practice" element={<Navigate to="/nextgen/practice/mcq" replace />} />

            {/* Browseable surfaces — directory/listing pages stay public so
                unentitled users can see what's inside before converting.
                These pages may render empty states or limited data when the
                user has no entitlement, but they don't 406 or time out.
                The actual content (taking questions, watching lessons,
                opening task details) is gated below. */}
            <Route path="/nextgen/coursework" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenCourseworkLibrary />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills" element={<Navigate to="/nextgen/skills/practice" replace />} />
            <Route path="/nextgen/skills/practice" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills/drills" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSkillsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/skills/mcq" element={<Navigate to="/nextgen/practice/mcq?formats=mcq,mcq_b" replace />} />
            <Route path="/nextgen/skills/iqs" element={<Navigate to="/nextgen/practice/iqs" replace />} />
            <Route path="/nextgen/skills/pt" element={<Navigate to="/nextgen/practice/pt" replace />} />
            <Route path="/nextgen/progress" element={<Navigate to="/nextgen/progress/overview" replace />} />
            <Route path="/nextgen/progress/overview" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/progress/readiness" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/progress/insights" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenProgressPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/simulations" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenSimulationsPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/exam-map" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenExamMapPage />
                </NextGenLayout>
              </Suspense>
            } />
            <Route path="/nextgen/dashboard" element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenLayout>
                  <NextGenDashboardPage />
                </NextGenLayout>
              </Suspense>
            } />
            {/* Coach — browseable. Unentitled users see the UI + greeting +
                suggestion chips but the input is replaced with a trial CTA
                (CoachPreviewNotice). Sending messages is gated in-page. */}
            <Route path="/nextgen/coach" element={
              <Suspense fallback={<PageLoader message="Loading Study Coach..." />}>
                <NextGenLayout>
                  <NextGenCoachPage />
                </NextGenLayout>
              </Suspense>
            } />

            {/* ============================================================== */}
            {/* UBE / MBE standalone bar-prep product — board chxw5cJrcarpNLY2HrDg */}
            {/* UbeLayout self-gates on the `ube-bar-prep` flag (FlagGate →       */}
            {/* Navigate to "/" when OFF). Sidebar entry-point additionally       */}
            {/* requires ube_package entitlement + active_products[] includes     */}
            {/* 'ube'. Tabs mirror NextGen (Skills / Simulations / Progress /     */}
            {/* Coach) per founder decision. Progress is fully built (subject     */}
            {/* axis); the other three land on an honest "coming soon" placeholder */}
            {/* until their UBE content/data pipeline lands (other windows' work). */}
            {/* ============================================================== */}
            {/* Browse-mode paywall (founder direction, board 87q2TNhDml6gO9d2BTp8):
                  OUTER gate = UbeLayout's <FlagGate flag="ube-bar-prep"> — flag OFF
                    → Navigate to "/" (whole section + nav disappear); flag ON →
                    section visible to EVERYONE (entitled and unentitled alike).
                  NO inner hard gate on these browse surfaces. The four functional
                    pages (Skills / Simulations / Progress / Coach) render for all
                    flag-on users; unentitled users see their analytics dashboard
                    with a <UbeBrowseModeCallout> nudge instead of a redirect.
                Mirrors NextGen exactly: NextGenContentGate (hard redirect) wraps
                ONLY act-on-content leaf surfaces (take a question, run a sim, deep
                coach) — never browse/analytics surfaces. UbeContentGate is kept in
                the codebase, reserved for those future Legacy interaction leaves. */}
            <Route path="/ube" element={<Navigate to="/ube/progress" replace />} />
            <Route path="/ube/skills" element={<Navigate to="/ube/skills/practice" replace />} />
            {/* PUBLIC pricing page — INSIDE UbeLayout's flag gate (so it only exists
                when ube-bar-prep is ON) but OUTSIDE UbeContentGate (so unentitled
                users can reach it to purchase). Auth-required for checkout +
                signal attribution. Uses its own PricingTopBar chrome, not the
                UbeLayout nav (matches /nextgen/pricing, which uses PricingTopBar
                rather than NextGenLayout). */}
            <Route path="/ube/pricing" element={
              <ProtectedRoute>
                <FlagGate flag="ube-bar-prep" fallback={<Navigate to="/" replace />} pending={<PageLoader message="Loading Legacy Bar Prep..." />}>
                  <NativePurchaseGuard>
                    <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                      <UbePricingPage />
                    </Suspense>
                  </NativePurchaseGuard>
                </FlagGate>
              </ProtectedRoute>
            } />
            <Route path="/ube/skills/practice" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeComingSoonPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/simulations" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeComingSoonPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/progress" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeProgressPage /></UbeLayout>
              </Suspense>
            } />
            <Route path="/ube/coach" element={
              <Suspense fallback={<PageLoader message="Loading Legacy Bar Prep..." />}>
                <UbeLayout><UbeCoachPage /></UbeLayout>
              </Suspense>
            } />

            {/* Entitlement-gated content — interaction/leaf surfaces only.
                Soft-paywall: NextGenContentGate redirects none/expired to
                /nextgen/pricing (the canonical sales/trial-activation page).
                These are the surfaces where the user *acts on* content:
                taking a question, opening a task detail, watching a lesson,
                running a simulation, chatting with the coach. */}
            <Route element={
              <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                <NextGenContentGate><Outlet /></NextGenContentGate>
              </Suspense>
            }>
              {/* Task detail — opening a specific task (original 406 source) */}
              <Route path="/nextgen/skills/:taskId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout>
                    <NextGenTaskDetail />
                  </NextGenLayout>
                </Suspense>
              } />
              {/* MCQ practice — taking questions */}
              <Route path="/nextgen/practice/mcq" element={
                <Suspense fallback={<PageLoader message="Loading practice..." />}>
                  <NextGenLayout><MCQPracticeWithReset /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/mcq/:questionId" element={
                <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenMCQSinglePage /></NextGenLayout></Suspense>
              } />
              {/* Coursework player — watching a specific lesson */}
              <Route path="/nextgen/coursework/:courseId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              <Route path="/nextgen/coursework/:courseId/:moduleId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              <Route path="/nextgen/coursework/:courseId/:moduleId/:lessonId" element={
                <NextGenLayout>
                  <NextGenCourseworkPlayer />
                </NextGenLayout>
              } />
              {/* Simulations — taking the sim + reviewing it */}
              <Route path="/nextgen/sim/:sectionNumber" element={
                <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenSectionSimPage /></NextGenLayout></Suspense>
              } />
              <Route path="/nextgen/sim/:sessionId/review" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout>
                    <NextGenSimReviewPage />
                  </NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/exam/:runId" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenFullExamPage /></Suspense>
                </FlagGate>
              } />
              <Route path="/nextgen/exam/:runId/results" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenLayout><NextGenExamResultsPage /></NextGenLayout></Suspense>
                </FlagGate>
              } />
              <Route path="/nextgen/sets/:setId" element={
                <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />}>
                  <Suspense fallback={<PageLoader />}><NextGenSetRedirect /></Suspense>
                </FlagGate>
              } />
              {/* IQS / PT / Drafting practice — taking questions */}
              <Route path="/nextgen/practice/iqs" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenIQSPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/iqs/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenIQSPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/pt" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenPTPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/pt/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenPTPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/drafting" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout>
                </Suspense>
              } />
              <Route path="/nextgen/practice/drafting/:setId" element={
                <Suspense fallback={<PageLoader message="Loading NextGen Prep..." />}>
                  <NextGenLayout><NextGenDraftingPracticePage /></NextGenLayout>
                </Suspense>
              } />
              {/* /nextgen/practice/counseling/:taskId route retired 2026-05-25 (board
                  RYu91USgyqQZ3W7gj4qi) — client_chat task type has zero rows. */}
              <Route path="/nextgen/practice/counseling/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
              {/* Workspace routes retired — redirect to practice system */}
              <Route path="/nextgen/workspace/:taskId" element={<Navigate to="/nextgen/practice/pt" replace />} />
              <Route path="/nextgen/workspace/:taskId/chat" element={<Navigate to="/nextgen/practice/pt" replace />} />
              <Route path="/nextgen/workspace/:taskId/results" element={<Navigate to="/nextgen/practice/pt" replace />} />
            </Route>
          </>
        )}

        {/* Case Collections Routes - Using FlashcardsPage structure */}
        {/* Cases System — unified under /cases/* — F.15 flag-gated by `case-brief-library` */}
        <Route path="/cases" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading cases..." />}>
              <CasesSearchPage />
            </Suspense>
          </FlagGate>
        } />
        <Route path="/cases/:id" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading case details..." />}>
              <CaseDetailPage />
            </Suspense>
          </FlagGate>
        } />
        <Route path="/cases/collections/*" element={
          <FlagGate flag="case-brief-library" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading case collections..." />}>
              <CaseBriefPage />
            </Suspense>
          </FlagGate>
        } />

        {/* Redirects for legacy case law and collection URLs */}
        <Route path="/case-law" element={<Navigate to="/cases" replace />} />
        <Route path="/case-law/*" element={<Navigate to="/cases" replace />} />
        <Route path="/case-collections" element={<Navigate to="/cases/collections" replace />} />
        <Route path="/case-collections/*" element={<Navigate to="/cases/collections" replace />} />
        
        {/* Question Bank Routes - DEPRECATED: Migrated to flashcards table */}
        {/* See docs/study-mode/QUESTION-BANK-CONSOLIDATION-PLAN.md */}
        {/* Redirect old Question Bank URLs to Unified Study */}
        <Route path="/questions/*" element={<Navigate to="/flashcards/unified-study" replace />} />

        
        {/* Notes Routes — F.15 flag-gated by `notes` */}
        <Route path="/notes" element={
          <FlagGate flag="notes" fallback={<Navigate to="/dashboard" replace />}>
            <Suspense fallback={<PageLoader message="Loading notes..." />}>
              <NotesPage />
            </Suspense>
          </FlagGate>
        } />

        {/* Community Routes */}
        <Route path="/community/*" element={
          <Suspense fallback={<PageLoader message="Loading community..." />}>
            <NavbarProvider>
              <CommunityPage />
            </NavbarProvider>
          </Suspense>
        } />
      </Route>
      
      {/* ============================================================== */}
      {/* Professor Console (314 Formative Assessment Kit) — board project */}
      {/* 8WpxviKObWe89vsP1hXq. FacultyLayout self-gates on the             */}
      {/* `faculty-console` flag (dev=ON / prod=OFF); flag OFF → Navigate  */}
      {/* to "/". Auth required; the faculty_console entitlement is        */}
      {/* enforced server-side by the faculty-console service. Faculty     */}
      {/* screens only — student consent + gated-result surfaces live in   */}
      {/* the normal student app, never under /faculty/* (spec §3.2).      */}
      {/* ============================================================== */}
      {[
        { path: '/faculty', El: FacultyClassesPage },
        { path: '/faculty/classes/new', El: FacultyClassSetupPage },
        { path: '/faculty/classes/:classId/settings', El: FacultyClassSetupPage },
        { path: '/faculty/classes/:classId/assignments/new', El: FacultyAssignmentBuilderPage },
        { path: '/faculty/classes/:classId/outcome-mapping', El: FacultyOutcomeMappingPage },
        { path: '/faculty/classes/:classId/analytics', El: FacultyAnalyticsPage },
        { path: '/faculty/classes/:classId/report', El: FacultyCompliancePage },
        { path: '/faculty/assignments/:assignmentId/edit', El: FacultyAssignmentBuilderPage },
        { path: '/faculty/assignments/:assignmentId/queue', El: FacultyReviewQueuePage },
        { path: '/faculty/assignments/:assignmentId/remediation', El: FacultyRemediationPage },
        { path: '/faculty/reviews/:submissionId', El: FacultyReviewPage },
        { path: '/faculty/desk', El: FacultyDeskPage },
        { path: '/faculty/classes/:classId/assignments', El: FacultyAssignmentsPage },
        { path: '/faculty/classes/:classId/students', El: FacultyClassStudentsPage },
        // Second of the TWO faculty route blocks (school domain above, main app
        // here) — a route registered in only one is reachable on only one domain.
        { path: '/faculty/classes/:classId/students/:userId', El: FacultyStudentProfilePage },
        { path: '/faculty/classes/:classId/materials', El: FacultyMaterialsPage },
        { path: '/faculty/classes/:classId/review', El: FacultyReviewInboxPage },
        { path: '/faculty/classes/:classId/discussion', El: FacultyClassDiscussionPage },
        { path: '/faculty/classes/:classId/discussion/:threadId', El: FacultyClassDiscussionPage },
        { path: '/faculty/room', El: FacultyRoomPage },
        { path: '/faculty/room/:threadId', El: FacultyRoomPage },
        { path: '/faculty/templates', El: FacultyTemplatesPage },
        { path: '/faculty/templates/from-syllabus', El: FacultyTemplateFromSyllabusPage },
        { path: '/faculty/reports', El: FacultyProgramReportsPage },
        { path: '/faculty/templates/:templateId', El: FacultyTemplateDetailPage },
        { path: '/faculty/templates/:templateId/deploy', El: FacultyTemplateDeployPage },
        { path: '/faculty/templates/:templateId/reports/:reportId', El: FacultyCurrencyReportPage },
      ].map(({ path, El }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader message="Loading Professor Console..." />}>
              <FacultyLayout><El /></FacultyLayout>
            </Suspense>
          </ProtectedRoute>
        } />
      ))}

      {/* Legacy course URLs — redirect to /courses/:courseId/... */}
      <Route path="/course/:courseId" element={<CourseRedirect />} />
      <Route path="/course/:courseId/module/:moduleId" element={<CourseRedirect />} />
      <Route path="/course/:courseId/module/:moduleId/lesson/:lessonId" element={<CourseRedirect />} />

      {/* Catch-all 404 route - must be last */}
      <Route path="*" element={
        <Suspense fallback={<PageLoader message="Loading..." />}>
          <NotFoundPage />
        </Suspense>
      } />
    </Routes>
    </>
  );
}

// Page loader component for suspense fallbacks
function PageLoader({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex flex-col justify-center items-center h-screen bg-white dark:bg-gray-900">
      <LoadingSpinner className="h-12 w-12 mb-4" />
      <p className="text-muted-foreground dark:text-gray-400">{message}</p>
    </div>
  );
}

/**
 * UTM Tracking Component
 * Must be rendered inside BrowserRouter for useLocation access.
 * Captures UTM parameters from URL and stores them for attribution.
 */
function UTMTrackingCapture() {
  useUTMTracking({
    trackArrival: true,
    debug: import.meta.env.DEV,
  });
  return null;
}

/**
 * Deep Link Initializer
 * Must be rendered inside BrowserRouter for useNavigate access.
 * Wires the native Capacitor deep-link handler (Universal Links, App Links,
 * custom scheme, Firebase auth callbacks) to React Router navigation.
 *
 * No-op on web: initializeDeepLinks() self-guards on isNativeApp and returns
 * early on non-native platforms, so this renders nothing and does nothing in
 * the browser build.
 */
function DeepLinkInitializer() {
  const navigate = useNavigate();
  useEffect(() => {
    initializeDeepLinks(navigate);
  }, [navigate]);
  return null;
}

// Wrapper function for the entire app
function AppWrapper() {
  // Read pinned state from localStorage synchronously to avoid flash:
  // unpinned users should start with sidebar collapsed, not expanded.
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('sidebar-is-pinned');
      return stored ? JSON.parse(stored) === true : false;
    } catch { return false; }
  });
  const [isExpanded, setIsExpanded] = useState(isPinned);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  
  // Initialize analytics tracking
  useAnalytics();

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-collapse sidebar on mobile
      if (window.innerWidth < 768) {
        setIsExpanded(false);
      }
    };
    
    // Initial check
    checkMobile();
    
    // Add listener for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    // Clean up potential auth-related localStorage items that might cause issues
    const keysToReset = [
      'protected_redirect_attempts',
      'auth_redirect_attempts'
    ];

    keysToReset.forEach(key => {
      try {
        if (sessionStorage.getItem(key)) {
          logger.debug(`Cleaning up ${key} from sessionStorage`);
          sessionStorage.removeItem(key);
        }
      } catch (e) {
        logger.warn(`Error cleaning up ${key}:`, e);
      }
    });

    // Remove prerendered SEO content from DOM now that React has mounted.
    // #seo-content is hidden by default via CSS — this removes it entirely.
    document.getElementById('seo-content')?.remove();
  }, []);
  
  return (
    <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <DomainProvider>
          <SubscriptionProvider>
            <LearningProfileProvider>
              <PaywallProvider>
              <CloseProvider>
                <SidebarContext value={{ isExpanded, setIsExpanded, isMobile, isPinned, setIsPinned }}>
                  <SelectedThreadContext value={{ selectedThreadId, setSelectedThreadId }}>
                    <ErrorBoundary>
                      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <TrialBannerProvider>
                          <UTMTrackingCapture />
                          <OpenAiPixelLoader />
                          <DeepLinkInitializer />
                          <div className="h-screen flex flex-col">
                            {/* Impersonation marker — top-most shell element so it
                                sits above every route/layout and pushes content
                                (incl. sticky navbars) down. Claim-driven; renders
                                null unless the session is an admin "view as user". */}
                            <ImpersonationBanner />
                            <GlobalTrialBanner />
                            <div className="flex-1 min-h-0 flex flex-col">
                              <AppRoutes />
                            </div>
                          </div>
                          <OnboardingModalHost />
                          <Toaster />
                          <HotToaster position="top-right" />
                          <OfflineIndicator />
                          <UpdateBanner />
                          <LayoutDebugger />
                        </TrialBannerProvider>
                      </BrowserRouter>
                    </ErrorBoundary>
                  </SelectedThreadContext>
                </SidebarContext>
              </CloseProvider>
              </PaywallProvider>
            </LearningProfileProvider>
          </SubscriptionProvider>
        </DomainProvider>
    </ThemeProvider>
  );
}

export default AppWrapper;// Test CI/CD deployment - Wed Aug 20 09:32:56 -05 2025
